<template>
  <div class="detail">
    <div class="head">
      <el-button
        class="back-btn"
        icon="el-icon-arrow-left"
        @click="goBack"
        plain
        >{{ $t("actions.back") }}</el-button
      >

      <div class="case-title">
        <h1>
          <span class="pid">{{ patient }}</span>
          <span class="sep">•</span>
          <span class="date">{{ date }}</span>
        </h1>
      </div>

      <div class="head-actions">
        <el-button size="mini" @click="fetchResults" :loading="loading" plain>{{ isEn ? 'Refresh' : '刷新' }}</el-button>
        <el-button size="mini" type="primary" plain @click="goL3Review">{{ isEn ? 'Back to L3 Annotation' : '返回L3标注页面' }}</el-button>
        <el-button size="mini" type="danger" @click="deleteCurrentCase">{{ isEn ? 'Delete Data' : '删除数据' }}</el-button>
      </div>
    </div>

    <section class="card" v-loading="loading">
      <div class="card-title">{{ $t("result.keyMetrics") }}</div>

      <div v-if="middleMetricItems.length" class="middle-metrics-list">
        <div
          v-for="item in middleMetricItems"
          :key="item.key"
          class="middle-metric-row"
        >
          <span class="middle-metric-label">{{ item.label }}</span>
          <span class="middle-metric-value">{{ item.value }}</span>
        </div>
      </div>

      <el-empty v-else :description="$t('result.emptyMetrics')" />
    </section>

    <section class="card">
      <div class="card-title">{{ isEn ? 'Analysis Calculation' : '分析计算' }}</div>
      <div class="analysis-panel">
        <div class="analysis-form">
          <label class="analysis-field">
            <span>{{ isEn ? 'Height (m)' : '身高(m)' }}</span>
            <el-input
              v-model="analysisForm.height"
              type="number"
              min="0"
              step="0.01"
              size="mini"
            />
          </label>
          <label class="analysis-field">
            <span>{{ isEn ? 'Weight (kg)' : '体重(kg)' }}</span>
            <el-input
              v-model="analysisForm.weight"
              type="number"
              min="0"
              step="0.1"
              size="mini"
            />
          </label>
          <label class="analysis-field">
            <span>{{ isEn ? 'Sex' : '性别' }}</span>
            <el-select v-model="analysisForm.sex" size="mini" :placeholder="isEn ? 'Select' : '请选择'">
              <el-option :label="isEn ? 'Male' : '男'" value="male" />
              <el-option :label="isEn ? 'Female' : '女'" value="female" />
            </el-select>
          </label>
          <label class="analysis-field">
            <span>{{ isEn ? 'Age' : '年龄' }}</span>
            <el-input
              v-model="analysisForm.age"
              type="number"
              min="0"
              max="130"
              step="1"
              size="mini"
            />
          </label>
        </div>
        <div class="analysis-results">
          <div
            v-for="item in analysisCalculationItems"
            :key="item.key"
            class="analysis-result-row"
          >
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </div>
        </div>
      </div>
      <div class="analysis-actions">
        <el-button
          type="primary"
          size="mini"
          :loading="analysisSaving"
          @click="saveAnalysisCalculation(true)"
        >
          {{ isEn ? 'Save' : '保存' }}
        </el-button>
      </div>
    </section>

    <section class="card">
      <div class="card-title">{{ $t("result.keyImages") }}</div>

      <div class="mid-ops" v-if="axisalMainName">
        <el-button
          size="mini"
          @click="openMiddleEditor"
          style="margin-top: 14px"
        >
          {{ $t("actions.manualMiddleMask") }}
        </el-button>
        <span class="middle-manual-tip">
          {{ isEn ? 'If manual annotation is needed, please re-annotate both psoas and total muscle.' : '如需手动标注，请同时重新标注腰大肌和全肌肉。' }}
        </span>
      </div>

      <div v-if="middleDisplayName" class="result-image-section">
        <div class="section-head">
          <h4>{{ isEn ? 'Selected Slice Result (Default: Middle Slice)' : '\u9009\u4e2d\u5207\u7247\u7ed3\u679c\uff08\u9ed8\u8ba4\u4e3a\u4e2d\u95f4\u5f20\uff09' }}</h4>
          <div v-if="middleSliceOptions.length" class="middle-slice-control">
            <span class="middle-slice-note">
              {{ isEn ? 'Change the final selected slice.' : '\u53ef\u66f4\u6362\u6700\u7ec8\u9009\u4e2d\u5207\u9762' }}
            </span>
            <el-select
              v-model="selectedMiddleSlice"
              size="mini"
              filterable
              :loading="selectingMiddleSlice"
              :placeholder="isEn ? 'Select slice' : '\u9009\u62e9\u5207\u7247'"
              class="middle-slice-select"
              @change="handleMiddleSliceChange"
            >
              <el-option
                v-for="img in middleSliceOptions"
                :key="img"
                :label="img"
                :value="img"
              />
            </el-select>
          </div>
        </div>
        <div class="middle-result-frame">
          <el-image
            class="middle-result-image"
            :src="middleDisplayUrl"
            fit="contain"
            :preview-src-list="[middleDisplayUrl]"
          />
          <div class="middle-result-caption">{{ middleDisplayName }}</div>
        </div>
      </div>

      <!-- 综合覆盖图(包含肌肉+脂肪) - 可折叠 -->
      <div v-if="allOverlayImages.length" class="result-image-section">
        <div class="section-head">
          <h4>{{ isEn ? 'All L3-level Results (Muscle + Fat)' : 'L3层面全部结果 (肌肉 + 脂肪)' }}</h4>
          <el-button
            size="mini"
            @click="allOverlayCollapsed = !allOverlayCollapsed"
            :icon="allOverlayCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"
          >
            {{ allOverlayCollapsed ? (isEn ? 'Expand' : '展开') : (isEn ? 'Collapse' : '收起') }} ({{ allOverlayImages.length }} {{ isEn ? 'images' : '张' }})
          </el-button>
        </div>
        <div v-show="!allOverlayCollapsed" class="img-grid">
          <div v-for="(img, index) in allOverlayImages" :key="img" class="img-item">
            <el-image
              :src="allOverlayPreviewList[index]"
              fit="cover"
              :preview-src-list="allOverlayPreviewList"
              lazy
            />
            <div class="caption">{{ img }}</div>
          </div>
        </div>
      </div>
    </section>

    <el-dialog
      :title="csvDialogTitle"
      :visible.sync="csvDialogVisible"
      width="86%"
      :modal="false"
      :close-on-click-modal="false"
    >
      <el-table
        :data="csvDialogRows"
        border
        size="mini"
        max-height="520"
        :empty-text="isEn ? 'No CSV data' : 'CSV 无数据'"
      >
        <el-table-column
          v-for="col in csvDialogColumns"
          :key="col"
          :prop="col"
          :label="col"
          min-width="120"
          show-overflow-tooltip
        />
      </el-table>
    </el-dialog>

    <!-- 3D可视化 -->
    <section class="card">
      <div class="card-title" style="display: flex; justify-content: space-between; align-items: center;">
        <span>{{ isEn ? '3D Model Visualization' : '3D模型可视化' }}</span>
        <div>
          <el-select
            v-model="selectedMaskType"
            size="mini"
            :placeholder="isEn ? 'Select Type' : '选择类型'"
            style="width: 120px; margin-right: 10px;"
            @change="check3DModelsAvailability"
          >
            <el-option :label="isEn ? 'Psoas' : '腰大肌'" value="psoas" />
            <el-option :label="isEn ? 'Total Muscle' : '全肌肉'" value="muscle" />
          </el-select>
        </div>
      </div>

      <!-- 提示信息 -->
      <div v-if="!show3DViewer && !reconstructing3D" style="padding: 20px; text-align: center; color: #999;">
        <i class="el-icon-info" style="font-size: 48px; margin-bottom: 10px;"></i>
        <p>{{ isEn ? 'No 3D model file is available for this case.' : '当前病例暂无可显示的3D模型文件' }}</p>
      </div>

      <!-- 3D查看器 -->
      <Model3DViewer
        v-if="show3DViewer && !reconstructing3D"
        :patient="patient"
        :date="date"
        :owner-id="ownerId"
        :selected-mask-type="selectedMaskType"
        :key="`3d-viewer-${selectedMaskType}-${modelRefreshKey}`"
      />
    </section>

    <section v-if="showSavedResults" class="card">
      <div class="card-title">{{ isEn ? 'Saved Result Files' : '保存的结果文件' }}</div>
      <el-tabs v-model="assetTab">
        <el-tab-pane :label="isEn ? 'Analysis Result Download' : '分析结果下载'" name="csv">
          <el-table
            :data="analysisDownloadAssets"
            border
            size="small"
            style="width: 100%"
            :empty-text="isEn ? 'No analysis files' : '暂无分析结果文件'"
          >
            <el-table-column :label="isEn ? 'Analysis Result' : '分析结果'" min-width="260">
              <template slot-scope="{ row }">{{ assetLabel(row) }}</template>
            </el-table-column>
            <el-table-column prop="filename" :label="isEn ? 'Original Filename' : '原始文件名'" min-width="220" />
            <el-table-column :label="isEn ? 'Action' : '操作'" width="180" align="center">
              <template slot-scope="{ row }">
                <el-button v-if="!row.is_zip" size="mini" type="primary" @click="openCsv(row)">{{ isEn ? 'View' : '查看' }}</el-button>
                <el-button size="mini" @click="downloadCsv(row)">{{ isEn ? 'Download' : '下载' }}</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

      </el-tabs>
      <div v-if="caseStatsRows.length" class="case-stats">
        <div class="sub-title stats-title">
          <span>{{ isEn ? 'Current Case Statistics' : '本次结果统计（当前病例）' }}</span>
          <el-button size="mini" type="primary" plain @click="downloadCaseStatsCsv">
            {{ isEn ? 'Download Statistics CSV' : '下载统计CSV' }}
          </el-button>
        </div>
        <el-table
          :data="caseStatsRows"
          border
          size="small"
          style="width: 100%; margin-bottom: 16px"
        >
          <el-table-column prop="label" :label="isEn ? 'Metric' : '指标'" min-width="240" />
          <el-table-column prop="mean" :label="isEn ? 'Mean' : '平均值'" width="160" />
          <el-table-column prop="median" :label="isEn ? 'Median' : '中位数'" width="160" />
          <el-table-column prop="count" :label="isEn ? 'Sample Count' : '样本数'" width="120" />
        </el-table>
      </div>
    </section>

    <middle-mask-editor
      v-if="axisalMainName"
      :axisal-filename="axisalMainName"
      :patient="patient"
      :date="date"
      :owner-id="ownerId"
      :visible.sync="middleEditorVisible"
      @uploaded="onMiddleUploaded"
    />
  </div>
</template>

<script>
import {
  getKeyResults,
  getAnalysisCalculation as getAnalysisCalculationApi,
  saveAnalysisCalculation as saveAnalysisCalculationApi,
  selectMiddleSlice,
  getImageUrl,
  getCaseAssets,
  downloadOutputFolder,
  deletePatient,
  getL3ImageUrl,
  getTaskStatus,
  check3DModels,
  reconstruct3D,
} from "@/api";
import MiddleMaskEditor from "./MiddleMaskEditor.vue";
import Model3DViewer from "./Model3DViewer.vue";
import axios from 'axios';

export default {
  name: "ResultDetail",
  components: { MiddleMaskEditor, Model3DViewer },
    data() {
    return {
      loading: true,
      csv_files: {},
      middle_images: [],
      allOverlayImages: [], // 综合覆盖图(肌肉+脂肪)
      assets: [],
      assetTab: "csv",
      csvDialogVisible: false,
      csvDialogTitle: "",
      csvDialogRows: [],
      csvDialogColumns: [],
      allOverlayCollapsed: true, // 综合覆盖图折叠状态
      rows: [],
      summary: null,
      analysisForm: {
        height: null,
        weight: null,
        sex: "",
        age: null,
      },
      analysisSaveTimer: null,
      analysisRestoring: false,
      analysisSaving: false,
      previewList: [],
      allOverlayPreviewList: [],
      l3Detecting: false,
      l3Continuing: false,
      l3ImageUrl: "",
      maskEditorVisible: false,
      middleEditorVisible: false,
      middleMainName: "", // slice_xxx_middle.png
      axisalMainName: "", // slice_xxx.png (用于原图标注)
      selectedMiddleSlice: "",
      savedMiddleSlice: "",
      selectingMiddleSlice: false,
      l3TaskId: null,
      l3PollTimer: null,
      l3Progress: 0,
      l3ProgressMessage: "",
      l3ImageUrlIndex: 0, // 当前尝试的L3图片路径索引
      l3PossibleUrls: [], // 所有可能的L3图片路径
      show3DViewer: false, // 是否显示3D可视化
      selectedMaskType: 'psoas', // 选择的掩码类型
      reconstructing3D: false, // 是否正在重建3D
      reconstruction3DProgress: 0, // 3D重建进度
      reconstruction3DMessage: '', // 3D重建消息
      reconstruction3DTaskId: null, // 3D重建任务ID
      reconstruction3DTimer: null, // 3D重建轮询定时器
      modelRefreshKey: 0, // 用于强制刷新3D模型组件
    };
  },
  beforeDestroy() {
    // 组件销毁时清除定时器
    if (this.l3PollTimer) {
      clearInterval(this.l3PollTimer);
    }
    if (this.reconstruction3DTimer) {
      clearInterval(this.reconstruction3DTimer);
    }
    if (this.analysisSaveTimer) {
      clearTimeout(this.analysisSaveTimer);
      this.saveAnalysisCalculation();
    }
  },
  computed: {
    isEn() {
      return this.$i18n.locale === "en";
    },
    patient() {
      return this.$route.params.patient;
    },
    date() {
      return this.$route.params.date;
    },
    ownerId() {
      return this.$route.query.owner_id || null;
    },
    showSavedResults() {
      return (
        this.$route.query.source === "management" ||
        this.caseStatsRows.length > 0 ||
        this.assets.length > 0
      );
    },
    csvAssets() {
      return this.assets.filter((item) => item.is_csv || item.type === "csv");
    },
    analysisDownloadAssets() {
      const items = [];
      if (this.allOverlayImages.length) {
        items.push({
          is_zip: true,
          folder: "all_overlay",
          filename: "all_overlay.zip",
          path: "all_overlay",
        });
      }
      return items.concat(this.csvAssets);
    },
    imageAssets() {
      return this.assets.filter((item) => item.preview);
    },
    middleSliceOptions() {
      return [...this.allOverlayImages].sort((a, b) => {
        const numA = Number((a.match(/\d+/) || [Number.MAX_SAFE_INTEGER])[0]);
        const numB = Number((b.match(/\d+/) || [Number.MAX_SAFE_INTEGER])[0]);
        if (numA !== numB) return numA - numB;
        return a.localeCompare(b);
      });
    },
    middleOverlayName() {
      if (!this.allOverlayImages.length) return "";
      const candidates = [];
      if (this.selectedMiddleSlice) candidates.push(this.selectedMiddleSlice);
      if (this.axisalMainName) candidates.push(this.axisalMainName);
      if (this.middleMainName) {
        candidates.push(this.middleMainName.replace(/_middle(\.[^.]+)$/i, "$1"));
        candidates.push(this.middleMainName.replace(/_middle/i, ""));
      }

      for (const name of candidates) {
        const found = this.allOverlayImages.find((img) => img.toLowerCase() === name.toLowerCase());
        if (found) return found;
      }

      const sliceMatch = (this.middleMainName || this.axisalMainName || "").match(/slice[_-]?\d+/i);
      if (sliceMatch) {
        const key = sliceMatch[0].toLowerCase();
        return this.allOverlayImages.find((img) => img.toLowerCase().includes(key)) || "";
      }
      return "";
    },
    middleDisplayName() {
      return this.middleOverlayName || this.middleMainName;
    },
    middleDisplayUrl() {
      if (this.middleOverlayName) {
        return getL3ImageUrl(this.patient, this.date, "all_overlay", this.middleOverlayName, true, this.ownerId);
      }
      return this.middleMainName ? this.imageUrl(this.middleMainName) : "";
    },
    middleMetricRow() {
      return this.rows && this.rows.length ? this.rows[0] : null;
    },
    heightSquared() {
      const height = Number(this.analysisForm.height);
      return height > 0 ? height * height : null;
    },
    psoasAreaCm2() {
      return this.middleMetricRow && typeof this.middleMetricRow.psoas_area_mm2 === "number"
        ? this.middleMetricRow.psoas_area_mm2 / 100
        : null;
    },
    comboAreaCm2() {
      return this.middleMetricRow && typeof this.middleMetricRow.combo_area_mm2 === "number"
        ? this.middleMetricRow.combo_area_mm2 / 100
        : null;
    },
    analysisCalculationItems() {
      const values = this.getAnalysisCalculationValues();
      return [
        { key: "bmi", label: "BMI", value: this.fmt(values.bmi) },
        { key: "tpi", label: "TPI", value: this.fmt(values.tpi) },
        { key: "smi", label: "SMI", value: this.fmt(values.smi) },
      ];
    },
    middleMetricItems() {
      const row = this.middleMetricRow;
      if (!row) return [];
      const metrics = [
        [this.isEn ? "Psoas HU(mean)" : "腰大肌 HU(mean)", "psoas_hu_mean"],
        [this.isEn ? "Psoas Area(cm²)" : "腰大肌面积(cm²)", "psoas_area_mm2", 100],
        [this.isEn ? "Psoas Volume(cm³)" : "腰大肌体积(cm³)", "major_volume_mm3", 1000],
        [this.isEn ? "Psoas Mass(g)" : "腰大肌质量(g)", "mass_psoas_g"],
        [this.isEn ? "Total Muscle HU(mean)" : "全肌肉 HU(mean)", "combo_hu_mean"],
        [this.isEn ? "Total Muscle Area(cm²)" : "全肌肉面积(cm²)", "combo_area_mm2", 100],
        [this.isEn ? "Total Muscle Volume(cm³)" : "全肌肉体积(cm³)", "full_volume_mm3", 1000],
        [this.isEn ? "Total Muscle Mass(g)" : "全肌肉质量(g)", "mass_combo_g"],
        [this.isEn ? "SAT HU(mean)" : "SAT HU(mean)", "sat_hu_mean"],
        [this.isEn ? "SAT Area(cm²)" : "SAT 面积(cm²)", "sat_area_mm2", 100],
        [this.isEn ? "VAT HU(min)" : "VAT HU(min)", "vat_hu_min"],
        [this.isEn ? "VAT Area(cm²)" : "VAT 面积(cm²)", "vat_area_mm2", 100],
      ];
      return metrics.map(([label, key, divisor]) => ({
        key,
        label,
        value: this.fmt(divisor ? row[key] / divisor : row[key]),
      }));
    },
    caseStatsRows() {
      const fullCsvName = Object.keys(this.csv_files || {}).find(
        (name) => /hu_statistics\.csv$/i.test(name) && !/middle[_-]?only/i.test(name)
      );
      const sourceRows = fullCsvName
        ? this.extractKeyRows(this.csv_files[fullCsvName])
        : this.rows;
      const metrics = [
        ["Psoas HU(mean)", "psoas_hu_mean"],
        [this.isEn ? "Psoas Area(mm²)" : "Psoas 面积(mm²)", "psoas_area_mm2"],
        [this.isEn ? "Psoas Volume(mm³)" : "腰大肌体积(mm³)", "major_volume_mm3"],
        ["Combo HU(mean)", "combo_hu_mean"],
        [this.isEn ? "Combo Area(mm²)" : "Combo 面积(mm²)", "combo_area_mm2"],
        [this.isEn ? "Total Muscle Volume(mm³)" : "全肌肉体积(mm³)", "full_volume_mm3"],
        ["SAT HU", "sat_hu_mean"],
        [this.isEn ? "SAT Area(mm²)" : "SAT 面积(mm²)", "sat_area_mm2"],
        ["VAT HU", "vat_hu_mean"],
        [this.isEn ? "VAT Area(mm²)" : "VAT 面积(mm²)", "vat_area_mm2"],
      ];
      return metrics
        .map(([label, key]) => {
          const values = (sourceRows || [])
            .map((row) => row[key])
            .filter((value) => typeof value === "number" && !Number.isNaN(value));
          if (!values.length) return null;
          return {
            label,
            mean: this.fmt(this.average(values)),
            median: this.fmt(this.median(values)),
            count: values.length,
          };
        })
        .filter(Boolean);
    },
    isL3HighlightImage() {
      // 判断当前显示的是否是verseg新方法生成的图片（已预翻转）
      return this.l3ImageUrl && (
        this.l3ImageUrl.includes('verseg/sagittal_midResize')
      );
    },
  },
  watch: {
    analysisForm: {
      deep: true,
      handler() {
        if (this.analysisRestoring) return;
        this.scheduleAnalysisSave();
      },
    },
  },
  async created() {
    await this.fetchResults();
    await this.loadAnalysisCalculation();
    // 检查是否有3D模型可用
    this.check3DModelsAvailability();
  },
  methods: {
    normalizeAnalysisForm(form = {}) {
      return {
        height: form.height == null ? null : form.height,
        weight: form.weight == null ? null : form.weight,
        sex: form.sex || "",
        age: form.age == null ? null : form.age,
      };
    },
    getAnalysisCalculationValues() {
      const weight = Number(this.analysisForm.weight);
      const height2 = this.heightSquared;
      return {
        bmi: weight > 0 && height2 ? weight / height2 : null,
        tpi: this.psoasAreaCm2 != null && height2 ? this.psoasAreaCm2 / height2 : null,
        smi: this.comboAreaCm2 != null && height2 ? this.comboAreaCm2 / height2 : null,
      };
    },
    async loadAnalysisCalculation() {
      try {
        this.analysisRestoring = true;
        const res = await getAnalysisCalculationApi(this.patient, this.date, this.ownerId);
        const data = res && res.data ? res.data.data : null;
        if (data && data.form) {
          this.analysisForm = this.normalizeAnalysisForm(data.form);
        }
      } catch (e) {
        console.warn("[analysis] load failed", e);
      } finally {
        this.$nextTick(() => {
          this.analysisRestoring = false;
        });
      }
    },
    scheduleAnalysisSave() {
      if (this.analysisSaveTimer) clearTimeout(this.analysisSaveTimer);
      this.analysisSaveTimer = setTimeout(() => {
        this.saveAnalysisCalculation();
      }, 600);
    },
    async saveAnalysisCalculation(showMessage = false) {
      if (this.analysisSaveTimer) {
        clearTimeout(this.analysisSaveTimer);
        this.analysisSaveTimer = null;
      }
      const payload = {
        form: this.normalizeAnalysisForm(this.analysisForm),
        calculations: this.getAnalysisCalculationValues(),
      };
      this.analysisSaving = true;
      try {
        await saveAnalysisCalculationApi(this.patient, this.date, payload, this.ownerId);
        if (showMessage) {
          this.$message.success(this.isEn ? "Saved" : "已保存");
        }
      } catch (e) {
        console.warn("[analysis] save failed", e);
        if (showMessage) {
          const detail = e.response && e.response.data && (e.response.data.detail || e.response.data.message);
          this.$message.error(detail || (this.isEn ? "Save failed" : "保存失败"));
        }
      } finally {
        this.analysisSaving = false;
      }
    },
    async fetchResults() {
      this.loading = true;
      try {
        const res = await getKeyResults(this.patient, this.date, this.ownerId);
        const data = (res && res.data) || {};
        this.csv_files = data.csv_files || {};
        this.middle_images = data.middle_images || [];
        this.previewList = this.middle_images.map((n) => this.imageUrl(n));

        // 加载综合覆盖图(肌肉+脂肪)
        await this.loadAllOverlayImages();
        await this.loadAssets();

        const keys = Object.keys(this.csv_files || {});
        const csvName = keys.find((n) => /middle[_-]?only/i.test(n)) || keys[0];
        if (csvName) {
          this.rows = this.extractKeyRows(this.csv_files[csvName]);
          this.summary = this.makeSummary(this.rows);
          // middle 主图名称：取第一行 filename (含 _middle.png)
          if (this.rows.length) {
            this.middleMainName = this.rows[0].filename;
          } else if (this.middle_images.length) {
            this.middleMainName = this.middle_images[0];
          }
        } else {
          this.rows = [];
          this.summary = null;
          this.middleMainName = this.middle_images[0] || "";
        }
        // 推导 axisal 原图名
        this.axisalMainName = this.middleMainName
          ? this.middleMainName.replace("_middle.png", ".png")
          : "";
        this.syncSelectedMiddleSlice();

        // 自动加载 L3 图片（如果存在）
        this.loadL3Image();
      } catch (e) {
        this.$message.error(this.$t("messages.fetchFail"));
      } finally {
        this.loading = false;
      }
    },
    async loadAssets() {
      try {
        const res = await getCaseAssets(this.patient, this.date, this.ownerId);
        this.assets = res.data && res.data.assets ? res.data.assets : [];
      } catch (e) {
        this.assets = [];
      }
    },
    async loadAllOverlayImages() {
      try {
        const BASE_URL = process.env.VUE_APP_BASE_URL || "http://localhost:4200";
        const token = localStorage.getItem('access_token');
        const params = new URLSearchParams();
        params.append('t', Date.now());
        if (token) {
          params.append('token', token);
        }
        if (this.ownerId) {
          params.append('owner_id', this.ownerId);
        }
        const url = `${BASE_URL}/list_output_folder/${encodeURIComponent(this.patient)}/${this.date}/all_overlay?${params.toString()}`;
        const res = await axios.get(url);
        if (res.data && res.data.files) {
          this.allOverlayImages = res.data.files.filter(f => f.endsWith('.png'));
          this.allOverlayPreviewList = this.allOverlayImages.map(img =>
            getL3ImageUrl(this.patient, this.date, 'all_overlay', img, true, this.ownerId)
          );
        }
      } catch (e) {
        // all_overlay 可能不存在,静默处理
        this.allOverlayImages = [];
      }
    },
    syncSelectedMiddleSlice() {
      const candidates = [];
      if (this.axisalMainName) candidates.push(this.axisalMainName);
      if (this.middleMainName) {
        candidates.push(this.middleMainName.replace(/_middle(\.[^.]+)$/i, "$1"));
        candidates.push(this.middleMainName.replace(/_middle/i, ""));
      }

      for (const name of candidates) {
        const found = this.allOverlayImages.find((img) => img.toLowerCase() === name.toLowerCase());
        if (found) {
          this.selectedMiddleSlice = found;
          this.savedMiddleSlice = found;
          return;
        }
      }

      const sliceMatch = (this.middleMainName || this.axisalMainName || "").match(/slice[_-]?\d+/i);
      if (sliceMatch) {
        const key = sliceMatch[0].toLowerCase();
        const found = this.allOverlayImages.find((img) => img.toLowerCase().includes(key));
        if (found) {
          this.selectedMiddleSlice = found;
          this.savedMiddleSlice = found;
          return;
        }
      }

      this.selectedMiddleSlice = "";
      this.savedMiddleSlice = "";
    },
    async handleMiddleSliceChange(filename) {
      if (!filename || this.selectingMiddleSlice || filename === this.savedMiddleSlice) return;
      const previous = this.savedMiddleSlice;
      this.selectingMiddleSlice = true;
      try {
        await selectMiddleSlice(this.patient, this.date, filename, this.ownerId);
        this.savedMiddleSlice = filename;
        this.$message.success(this.isEn ? "Selected slice updated" : "\u9009\u4e2d\u5207\u7247\u5df2\u66f4\u65b0");
        await this.fetchResults();
        this.scheduleAnalysisSave();
      } catch (e) {
        this.selectedMiddleSlice = previous;
        const detail = e.response && e.response.data && e.response.data.detail;
        this.$message.error(detail || (this.isEn ? "Failed to update selected slice" : "\u66f4\u65b0\u9009\u4e2d\u5207\u7247\u5931\u8d25"));
      } finally {
        this.selectingMiddleSlice = false;
      }
    },
    openMiddleEditor() {
      this.middleEditorVisible = true;
    },
    async onMiddleUploaded() {
      // 重新取最新 middle 相关数据
      await this.fetchResults();
    },
    openMaskEditor() {
      this.maskEditorVisible = true;
    },
    onMaskUploaded(payload) {
      if (payload && payload.overlay) {
        this.setL3Overlay(payload.overlay);
      } else {
        this.$message.success(this.$t("messages.maskUploadSuccess"));
        this.loadL3Image();
      }
      this.assetTab = "csv";
      this.loadAssets();
    },
    extractKeyRows(csvText) {
      if (!csvText) return [];
      const lines = csvText.trim().split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) return [];
      const headers = lines[0].split(",").map((h) => h.trim());
      const idx = (name) =>
        headers.findIndex((h) => h.toLowerCase() === name.toLowerCase());
      const keyCols = {
        filename: "filename",
        psoas_hu_mean: "psoas_hu_mean",
        psoas_area_mm2: "psoas_area_mm2",
        combo_hu_mean: "combo_hu_mean",
        combo_area_mm2: "combo_area_mm2",
        sat_hu_mean: "sat_hu_mean",
        sat_area_mm2: "sat_area_mm2",
        vat_hu_mean: "vat_hu_mean",
        vat_hu_min: "vat_hu_min",
        vat_area_mm2: "vat_area_mm2",
        major_volume_mm3: "major_volume_mm3",
        full_volume_mm3: "full_volume_mm3",
        mass_psoas_g: "mass_psoas_g",
        mass_combo_g: "mass_combo_g",
      };
      const col = {};
      for (const k in keyCols) col[k] = idx(keyCols[k]);
      return lines
        .slice(1)
        .map((line) => {
          const cells = line.split(",").map((c) => c.trim());
          const get = (i) => (i >= 0 && i < cells.length ? cells[i] : "");
          const num = (v) => (v === "" ? null : Number(v));
          return {
            filename: get(col.filename),
            psoas_hu_mean: num(get(col.psoas_hu_mean)),
            psoas_area_mm2: num(get(col.psoas_area_mm2)),
            combo_hu_mean: num(get(col.combo_hu_mean)),
            combo_area_mm2: num(get(col.combo_area_mm2)),
            sat_hu_mean: num(get(col.sat_hu_mean)),
            sat_area_mm2: num(get(col.sat_area_mm2)),
            vat_hu_mean: num(get(col.vat_hu_mean)),
            vat_hu_min: num(get(col.vat_hu_min)),
            vat_area_mm2: num(get(col.vat_area_mm2)),
            major_volume_mm3: num(get(col.major_volume_mm3)),
            full_volume_mm3: num(get(col.full_volume_mm3)),
            mass_psoas_g: num(get(col.mass_psoas_g)),
            mass_combo_g: num(get(col.mass_combo_g)),
          };
        })
        .filter((r) => r.filename);
    },
    makeSummary(rows) {
      if (!rows.length) return null;
      const keys = [
        "psoas_hu_mean",
        "psoas_area_mm2",
        "combo_hu_mean",
        "combo_area_mm2",
        "sat_hu_mean",
        "sat_area_mm2",
        "vat_hu_mean",
        "vat_area_mm2",
      ];
      const s = {};

      // 计算平均值
      for (const k of keys) {
        const vals = rows
          .map((r) => r[k])
          .filter((v) => typeof v === "number" && !Number.isNaN(v));
        s[k] = vals.length
          ? vals.reduce((a, b) => a + b, 0) / vals.length
          : null;
      }

      // 体积和质量数据(所有行都相同,取第一行)
      if (rows[0]) {
        s.major_volume_mm3 = rows[0].major_volume_mm3;
        s.full_volume_mm3 = rows[0].full_volume_mm3;
        s.mass_psoas_g = rows[0].mass_psoas_g;
        s.mass_combo_g = rows[0].mass_combo_g;
      }

      return s;
    },
    average(values) {
      return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
    },
    median(values) {
      if (!values.length) return null;
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
    },
    hasColumn(colName) {
      return this.rows.length > 0 && this.rows[0][colName] != null;
    },
    fmtVolume(mm3) {
      if (mm3 == null || Number.isNaN(mm3)) return '-';
      const ml = mm3 / 1000;
      return `${mm3.toFixed(0)} mm³ (${ml.toFixed(2)} mL)`;
    },
    imageUrl(filename) {
      return getImageUrl(this.patient, this.date, filename, this.ownerId);
    },
    assetUrl(row) {
      if (!row || !row.preview || row.folder === "input") return "";
      return getL3ImageUrl(this.patient, this.date, row.folder, row.filename, false, this.ownerId);
    },
    assetCategory(row) {
      const folder = row && row.folder ? row.folder.split("/")[0] : "";
      const map = this.isEn
        ? {
            L3_mask: "Manual L3 Annotation",
            L3_clean_mask: "Cleaned L3 Annotation",
            L3_overlay: "L3 Annotation Overlay",
            verseg: "L3 Post-processing Result",
            manual_middle_mask: "Manual Psoas / Muscle Annotation",
          }
        : {
            L3_mask: "手动 L3 标注",
            L3_clean_mask: "L3 标注清理结果",
            L3_overlay: "L3 标注叠加图",
            verseg: "L3 后续流程结果",
            manual_middle_mask: "手动腰大肌/全肌肉标注",
          };
      return map[folder] || folder || "-";
    },
    assetLabel(row) {
      if (!row) return "-";
      if (row.is_zip && row.folder === "all_overlay") {
        return this.isEn ? "Muscle segmentation images" : "肌肉分割图";
      }
      const path = row.path || `${row.folder}/${row.filename}`;
      if (path === "full_overlay/hu_statistics.csv") return this.isEn ? "Full metrics CSV generated automatically" : "自动处理生成的全量指标 CSV";
      if (path === "full_overlay/hu_statistics_middle_only.csv") return this.isEn ? "Current key-slice metrics CSV" : "当前关键切片指标 CSV";
      if (row.folder === "L3_mask") return this.isEn ? "L3 mask manually uploaded/drawn by user" : "用户手动上传/绘制的 L3 mask";
      if (row.folder === "L3_clean_mask") return this.isEn ? "Cleaned L3 mask" : "清理后的 L3 mask";
      if (row.folder === "L3_overlay") return this.isEn ? "Saved image after overlaying L3 mask on sagittal image" : "L3 mask 与侧位图叠加后的保存图";
      if (row.folder === "manual_middle_mask") {
        if (row.filename.includes("_psoas")) return this.isEn ? "Manually annotated psoas mask" : "用户手动标注的腰大肌 mask";
        if (row.filename.includes("_combo")) return this.isEn ? "Manually annotated total muscle mask" : "用户手动标注的全肌肉 mask";
        return this.isEn ? "Manually annotated muscle mask" : "用户手动标注的肌肉 mask";
      }
      if (row.folder === "verseg") {
        if (row.filename.includes("L3_highlight")) return this.isEn ? "L3 highlighted localization image from post-processing" : "后续流程生成的 L3 高亮定位图";
        if (row.filename.includes("L3_overlay")) return this.isEn ? "L3 overlay image from post-processing" : "后续流程生成的 L3 叠加图";
        if (row.filename.includes("L3_mask")) return this.isEn ? "L3 mask from post-processing" : "后续流程生成的 L3 mask";
        if (row.filename.includes("vertebra_overlay")) return this.isEn ? "Vertebra overlay image from post-processing" : "后续流程生成的椎体叠加图";
        if (row.filename.includes("whole_overlay")) return this.isEn ? "Whole segmentation overlay from post-processing" : "后续流程生成的整体分割叠加图";
      }
      return path;
    },
    getCsvContent(row) {
      const keys = [
        row.path,
        row.filename,
        `full_overlay/${row.filename}`,
        `output/${row.filename}`,
      ].filter(Boolean);
      let content = keys.map((key) => this.csv_files[key]).find(Boolean);
      if (!content) {
        const match = Object.keys(this.csv_files || {}).find((key) => key.endsWith(`/${row.filename}`));
        content = match ? this.csv_files[match] : "";
      }
      return content;
    },
    async openCsv(row) {
      let content = this.getCsvContent(row);
      if (!content) {
        const res = await getKeyResults(this.patient, this.date, this.ownerId);
        this.csv_files = (res && res.data && res.data.csv_files) || {};
        content = this.getCsvContent(row);
      }
      if (!content) {
        this.$message.warning("CSV 内容未加载");
        return;
      }
      const parsed = this.parseCsv(content);
      this.csvDialogTitle = `${row.folder}/${row.filename}`;
      this.csvDialogColumns = parsed.columns;
      this.csvDialogRows = parsed.rows;
      this.csvDialogVisible = true;
    },
    async downloadCsv(row) {
      if (row && row.is_zip) {
        await this.downloadOutputFolderZip(row);
        return;
      }
      let content = this.getCsvContent(row);
      if (!content) {
        const res = await getKeyResults(this.patient, this.date, this.ownerId);
        this.csv_files = (res && res.data && res.data.csv_files) || {};
        content = this.getCsvContent(row);
      }
      if (!content) {
        this.$message.warning("CSV 内容未加载");
        return;
      }
      const filename = row.filename
        ? `${this.patient}_${this.date}_${row.filename}`
        : `${this.patient}_${this.date}_result.csv`;
      this.downloadTextFile(content, filename);
    },
    async downloadOutputFolderZip(row) {
      try {
        const res = await downloadOutputFolder(this.patient, this.date, row.folder, this.ownerId);
        const filename = `${this.patient}_${this.date}_${row.folder}.zip`;
        this.downloadBlobFile(res.data, filename);
      } catch (e) {
        this.$message.error(this.isEn ? "Failed to download images" : "下载图片失败");
      }
    },
    downloadBlobFile(blobData, filename) {
      const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    downloadCaseStatsCsv() {
      const header = ["指标", "平均值", "中位数", "样本数"];
      const escapeCell = (value) => `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;
      const lines = [
        header.map(escapeCell).join(","),
        ...this.caseStatsRows.map((row) =>
          [row.label, row.mean, row.median, row.count].map(escapeCell).join(",")
        ),
      ];
      this.downloadTextFile(lines.join("\r\n"), `${this.patient}_${this.date}.csv`);
    },
    downloadTextFile(content, filename) {
      const blob = new Blob(["\uFEFF", content], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    parseCsv(text) {
      const lines = (text || "").trim().split(/\r?\n/).filter(Boolean);
      if (!lines.length) return { columns: [], rows: [] };
      const parseLine = (line) => line.split(",").map((cell) => cell.trim());
      const columns = parseLine(lines[0]);
      const rows = lines.slice(1).map((line) => {
        const values = parseLine(line);
        const row = {};
        columns.forEach((col, index) => {
          row[col] = values[index] || "";
        });
        return row;
      });
      return { columns, rows };
    },
    getL3ImageUrl(patient, date, folder, filename) {
      return getL3ImageUrl(patient, date, folder, filename, false, this.ownerId);
    },
    fmt(n) {
      return n == null || Number.isNaN(n) ? "-" : Number(n).toFixed(2);
    },
    fmtCell(row, col, cellValue) {
      return this.fmt(cellValue);
    },
    goBack() {
      this.$router.push("/");
    },
    goL3Review() {
      this.$router.push({
        path: "/",
        query: {
          patient: this.patient,
          date: this.date,
          l3: "1",
          manual_l3: "1",
          result_ready: "1",
        },
      });
    },
    goList() {
      if (this.$route.path !== "/results") this.$router.push("/results");
    },
    async deleteCurrentCase() {
      try {
        await this.$confirm(`确定删除 ${this.patient}•${this.date} 的全部数据吗？`, "提示", {
          type: "warning",
        });
        if (this.ownerId) {
          const BASE_URL = process.env.VUE_APP_BASE_URL || "http://localhost:4200";
          await axios.delete(
            `${BASE_URL}/admin/patients/${encodeURIComponent(this.patient)}/${this.date}?user_id=${encodeURIComponent(this.ownerId)}`
          );
        } else {
          await deletePatient(this.patient, this.date);
        }
        this.$message.success("已删除");
        this.$router.push("/results");
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error(e.response?.data?.detail || "删除失败");
        }
      }
    },
    async handleL3Detect() {
      this.l3Detecting = true;
      try {
        const res = await l3Detect(this.patient, this.date);
        this.$message.success(this.$t("messages.l3DetectSuccess"));
        if (res && res.data && res.data.l3_overlay) {
          this.setL3Overlay(res.data.l3_overlay);
        } else {
          this.loadL3Image();
        }
      } catch (e) {
        this.$message.error(this.$t("messages.l3DetectFail"));
      } finally {
        this.l3Detecting = false;
      }
    },
    beforeMaskUpload(file) {
      if (!file.name.endsWith(".png")) {
        this.$message.error(this.$t("messages.pngOnly"));
        return false;
      }
      return true;
    },
    async customMaskUpload({ file, onSuccess, onError }) {
      try {
        await uploadL3Mask(this.patient, this.date, file);
        this.$message.success(this.$t("messages.maskUploadSuccess"));
        this.loadL3Image();
        onSuccess();
      } catch (e) {
        this.$message.error(this.$t("messages.maskUploadFail"));
        onError(e);
      }
    },
    onMaskUploadSuccess() {},
    async handleContinueAfterL3() {
      this.l3Continuing = true;
      this.l3Progress = 0;
      this.l3ProgressMessage = this.$t("messages.waitProcess");

      try {
        // 1. 提交任务
        const res = await continueAfterL3(this.patient, this.date);

        if (res && res.data && res.data.task_id) {
          this.l3TaskId = res.data.task_id;
          this.$message.success(
            res.data.message || this.$t("messages.uploadSuccess")
          );

          // 2. 开始轮询任务状态
          this.startL3Polling();
        } else {
          // 向后兼容：同步处理
          this.$message.success(this.$t("messages.l3ContinueSuccess"));
          if (res && res.data && res.data.l3_overlay) {
            this.setL3Overlay(res.data.l3_overlay);
          } else {
            this.loadL3Image();
          }
          await this.fetchResults();
          this.l3Continuing = false;
        }
      } catch (e) {
        // 处理 409 冲突错误（任务已提交或僵尸任务）
        if (e.response && e.response.status === 409) {
          const msg =
            (e.response.data && e.response.data.detail) ||
            this.$t("messages.l3ContinueFail");
          this.$message.warning(msg);
        } else {
          this.$message.error(this.$t("messages.l3ContinueFail"));
        }
        this.l3Continuing = false;
      }
    },

    startL3Polling() {
      // 每 5 秒查询一次任务状态
      this.l3PollTimer = setInterval(async () => {
        try {
          const res = await getTaskStatus(this.l3TaskId);
          const status = res.data;

          this.l3Progress = status.progress || 0;
          this.l3ProgressMessage = status.message || "";

          // 利用新增的时间戳字段显示运行时长
          if (status.started_at && status.status === "processing") {
            const elapsed = Math.floor(Date.now() / 1000 - status.started_at);
            this.l3ProgressMessage += ` (已运行 ${elapsed}秒)`;

            // 超过 5 分钟提示可能卡死
            if (elapsed > 300) {
              this.l3ProgressMessage += " - 任务运行时间过长，可能已卡死";
            }
          }

          if (status.status === "completed") {
            // 任务完成
            clearInterval(this.l3PollTimer);
            const duration = status.duration
              ? `耗时 ${Math.round(status.duration)}秒`
              : "";
            this.$message.success(
              this.$t("messages.l3ContinueSuccess") +
                (duration ? ` (${duration})` : "")
            );

            // 处理结果
            if (status.result && status.result.l3_overlay) {
              this.setL3Overlay(status.result.l3_overlay);
            } else {
              this.loadL3Image();
            }

            // 刷新数据
            await this.fetchResults();
            this.assetTab = "csv";
            this.l3Continuing = false;
          } else if (status.status === "failed") {
            // 任务失败
            clearInterval(this.l3PollTimer);
            const errMsg = status.error ? `: ${status.error}` : "";
            this.$message.error(this.$t("messages.l3ContinueFail") + errMsg);
            this.l3Continuing = false;
          }
          // status === "processing" 时继续轮询
        } catch (e) {
          clearInterval(this.l3PollTimer);
          this.$message.error(this.$t("messages.l3ContinueFail"));
          this.l3Continuing = false;
        }
      }, 5000); // 5 秒轮询一次
    },
    loadL3Image() {
      // 定义所有可能的L3图片位置（按优先级排序）
      const possibleLocations = [
        // verseg新方法生成的文件 - 高亮版本（最优先）
        { folder: 'verseg', filename: 'sagittal_midResize_0000_L3_highlight.png' },
        { folder: 'verseg', filename: 'sagittal_midResize_0000_L3_overlay.png' },
        { folder: 'verseg', filename: 'sagittal_midResize_0000_whole_overlay.png' },
        { folder: 'verseg', filename: 'sagittal_midResize_0000_vertebra_overlay.png' },
        // 旧方法可能的位置
        { folder: 'verseg', filename: 'L3_clean.png' },
        { folder: 'verseg', filename: 'L3_overlay.png' },
        { folder: 'L3_overlay', filename: 'L3_clean.png' },
        { folder: 'L3_clean', filename: 'L3_clean.png' },
        { folder: 'L3', filename: 'L3_clean.png' },
        { folder: 'L3', filename: 'L3_overlay.png' },
      ];

      // 生成所有可能的URL
      this.l3PossibleUrls = possibleLocations.map(loc =>
        getL3ImageUrl(this.patient, this.date, loc.folder, loc.filename, false, this.ownerId)
      );

      // 从第一个开始尝试
      this.l3ImageUrlIndex = 0;
      this.tryNextL3Image();
    },
    tryNextL3Image() {
      if (this.l3ImageUrlIndex < this.l3PossibleUrls.length) {
        this.l3ImageUrl = this.l3PossibleUrls[this.l3ImageUrlIndex];
      } else {
        // 所有位置都尝试过了，没有找到图片
        this.l3ImageUrl = "";
      }
    },
    handleL3ImageError() {
      // 当前图片加载失败，尝试下一个位置
      this.l3ImageUrlIndex++;
      this.tryNextL3Image();
    },
    setL3Overlay(relPath) {
      if (!relPath) return;
      const parts = relPath.split("/");
      const folder = parts.shift();
      const filename = parts.join("/") || "L3_clean.png";
      // 强制刷新：使用缓存破坏
      this.l3ImageUrl = getL3ImageUrl(this.patient, this.date, folder, filename, true, this.ownerId);
    },
    versionedL3Url(folder, filename) {
      // L3 图片需要缓存破坏，因为会被更新
      return getL3ImageUrl(this.patient, this.date, folder, filename, true, this.ownerId);
    },
    async check3DModelsAvailability() {
      try {
        console.log('[3D检查] ========== 开始检查3D模型可用性 ==========');
        console.log('[3D检查] 当前状态:', {
          patient: this.patient,
          date: this.date,
          selectedMaskType: this.selectedMaskType,
          show3DViewer: this.show3DViewer,
          reconstructing3D: this.reconstructing3D
        });

        const result = await check3DModels(this.patient, this.date, this.ownerId);
        console.log('[3D检查] API返回结果:', result);

        if (result.available && result.models && result.models.length > 0) {
          console.log('[3D检查] ✅ 找到模型，数量:', result.models.length);
          console.log('[3D检查] 模型详情:', result.models);

          // 检查是否有当前选择类型的模型
          const hasSelectedType = result.models.some(m => m.mask_type === this.selectedMaskType);
          console.log('[3D检查] 是否有选中类型的模型:', hasSelectedType);
          console.log('[3D检查] 选中的类型:', this.selectedMaskType);

          console.log('[3D检查] 设置 show3DViewer =', hasSelectedType);
          this.show3DViewer = hasSelectedType;
          console.log('[3D检查] show3DViewer 已设置为:', this.show3DViewer);

          if (hasSelectedType) {
            // 找到对应模型的体积信息
            const model = result.models.find(m => m.mask_type === this.selectedMaskType);
            console.log('[3D检查] 找到对应模型:', model);
            if (model && model.volume_mm3) {
              console.log(`[3D检查] ${this.selectedMaskType} 体积: ${model.volume_mm3} mm³ (${model.volume_ml} mL)`);
            }
          } else {
            console.warn('[3D检查] ⚠️ 没有找到 ' + this.selectedMaskType + ' 类型的模型');
          }
        } else {
          console.log('[3D检查] ❌ 没有可用的3D模型');
          this.show3DViewer = false;
        }

        console.log('[3D检查] ========== 检查完成 ==========');
        console.log('[3D检查] 最终状态:', {
          show3DViewer: this.show3DViewer,
          reconstructing3D: this.reconstructing3D
        });

        // 强制 Vue 更新 DOM
        await this.$nextTick();
        console.log('[3D检查] $nextTick 完成，强制刷新...');
        this.$forceUpdate();
        console.log('[3D检查] 强制刷新完成');

        // 验证组件是否已挂载
        await this.$nextTick();
        const viewer = this.$el.querySelector('.model-3d-viewer');
        console.log('[3D检查] 查找 3D 查看器 DOM 元素:', viewer ? '✅ 找到' : '❌ 未找到');
      } catch (error) {
        console.error('[3D检查] ❌ 检查3D模型失败:', error);
        this.show3DViewer = false;
      }
    },
    async trigger3DReconstruction() {
      this.reconstructing3D = true;
      this.reconstruction3DProgress = 0;
      this.reconstruction3DMessage = '正在提交3D重建任务...';

      try {
        const result = await reconstruct3D(this.patient, this.date, this.selectedMaskType);

        if (result.task_id) {
          this.reconstruction3DTaskId = result.task_id;
          this.$message.success(result.message || '3D重建任务已提交');

          // 开始轮询任务状态
          this.start3DReconstructionPolling();
        } else {
          // 同步完成（不太可能）
          this.$message.success('3D重建完成');
          this.reconstructing3D = false;
          await this.check3DModelsAvailability();
          this.modelRefreshKey++; // 强制刷新3D查看器
        }
      } catch (error) {
        console.error('3D重建失败:', error);
        const errorMsg = error.response?.data?.detail || error.message || '3D重建失败';
        this.$message.error(errorMsg);
        this.reconstructing3D = false;
      }
    },
    start3DReconstructionPolling() {
      // 每3秒查询一次任务状态
      this.reconstruction3DTimer = setInterval(async () => {
        try {
          const res = await getTaskStatus(this.reconstruction3DTaskId);
          const status = res.data;

          this.reconstruction3DProgress = status.progress || 0;
          this.reconstruction3DMessage = status.message || '';

          if (status.status === 'completed') {
            // 任务完成
            clearInterval(this.reconstruction3DTimer);
            const duration = status.duration ? `耗时 ${Math.round(status.duration)}秒` : '';
            this.$message.success(`3D重建完成 ${duration}`);

            // 刷新3D模型列表
            await this.check3DModelsAvailability();
            this.modelRefreshKey++; // 强制刷新3D查看器
            this.reconstructing3D = false;
          } else if (status.status === 'failed') {
            // 任务失败
            clearInterval(this.reconstruction3DTimer);
            const errMsg = status.error ? `: ${status.error}` : '';
            this.$message.error(`3D重建失败${errMsg}`);
            this.reconstructing3D = false;
          }
          // status === "processing" 时继续轮询
        } catch (e) {
          clearInterval(this.reconstruction3DTimer);
          this.$message.error('查询3D重建状态失败');
          this.reconstructing3D = false;
        }
      }, 3000); // 3秒轮询一次
    },
  },
};
</script>

<style scoped>
.detail {
  width: clamp(960px, 86vw, 1140px);
  margin: 24px auto 80px;
}

.title {
  display: inline-block;
  padding: 8px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  color: #0f172a;
  font-weight: 800;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}
.head {
  display: flex;
  align-items: center;
  gap: 30px;
  flex-wrap: wrap;
  margin-bottom: 26px;
  padding: 14px 28px 16px;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 26px;
  box-shadow: 0 8px 28px -10px rgba(0, 0, 0, 0.1);
}
.card {
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px 18px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  margin-bottom: 18px;
  color: #111827;
}
.card-title {
  font-weight: 800;
  margin-bottom: 8px;
  color: #0f172a;
}
.sub-title {
  font-weight: 700;
  margin: 8px 0 10px;
  color: #0f172a;
}
.stats-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.middle-metrics-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: repeat(4, auto);
  grid-auto-flow: column;
  gap: 0;
  margin-top: 12px;
  border-top: 1px solid #e5edf5;
}
.middle-metric-row {
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  padding: 7px 10px;
  background: #fff;
  border-bottom: 1px solid #e5edf5;
}
.middle-metric-row:nth-child(-n + 8) {
  border-right: 1px solid #d7e1ea;
  padding-right: 18px;
}
.middle-metric-row:nth-child(n + 5) {
  padding-left: 18px;
}
.middle-metric-label {
  color: #526273;
  font-size: 12px;
  line-height: 1.3;
  min-width: 145px;
}
.middle-metric-value {
  color: #0f172a;
  font-weight: 700;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  text-align: left;
  white-space: nowrap;
}
.analysis-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px minmax(0, 1fr);
  gap: 18px;
  margin-top: 12px;
}
.analysis-form {
  grid-column: 1;
  display: grid;
  grid-template-columns: repeat(2, 160px);
  justify-content: start;
  gap: 10px 12px;
}
.analysis-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  color: #526273;
  font-size: 12px;
}
.analysis-field /deep/ .el-input,
.analysis-field /deep/ .el-select {
  width: 150px;
}
.analysis-field /deep/ .el-input__inner {
  height: 26px;
  line-height: 26px;
  padding: 0 8px;
  font-size: 12px;
}
.analysis-field /deep/ .el-input__icon {
  line-height: 26px;
}
.analysis-results {
  grid-column: 2;
  border-top: 1px solid #e5edf5;
}
.analysis-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-start;
}
.analysis-result-row {
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 7px 10px;
  border-bottom: 1px solid #e5edf5;
  color: #526273;
  font-size: 12px;
}
.analysis-result-row strong {
  color: #0f172a;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px 12px;
}
.pill {
  background: #f6f9ff;
  border: 1px solid #e6eeff;
  border-radius: 999px;
  padding: 8px 14px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}
.pill .k {
  color: #5a6;
  opacity: 0.9;
}
.pill .v {
  font-weight: 700;
  color: #1f2;
}

.img-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}
.img-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f9fafb;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
}
.img-item .el-image {
  width: 100%;
  height: 180px;
}
.caption {
  position: absolute;
  left: 8px;
  bottom: 8px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 999px;
}
.mid-ops {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.middle-manual-tip {
  margin-top: 14px;
  color: #7a8695;
  font-size: 13px;
  line-height: 1.4;
}
.result-image-section {
  margin-top: 20px;
}
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.section-head h4 {
  margin: 0;
  font-size: 14px;
  color: #4b5b68;
}
.middle-slice-control {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.middle-slice-note {
  font-size: 12px;
  color: #66788a;
  line-height: 1.3;
}
.middle-slice-select {
  width: 180px;
}
.img-grid.single {
  grid-template-columns: minmax(240px, 420px);
}
.middle-result-frame {
  width: min(100%, 320px);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
}
.middle-result-image {
  width: 100%;
  display: block;
  background: #000;
}
.middle-result-image /deep/ .el-image__inner {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}
.middle-result-caption {
  padding: 8px 12px;
  background: #f8fafc;
  color: #4b5b68;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-all;
}
.l3-preview {
  margin-top: 12px;
}
.l3-preview-img {
  max-width: 300px;
  display: block;
  transform: rotate(180deg) scaleX(-1);
  transform-origin: center;
}
.l3-preview-img.no-flip {
  /* highlight图片已在后端预翻转，不需要前端再翻转 */
  transform: none;
}
.l3-preview-tip {
  font-size: 12px;
  color: #888;
  margin-top: 6px;
}
.asset-thumb {
  width: 72px;
  height: 48px;
  border-radius: 6px;
  background: #f1f5f9;
}
@media (max-width: 760px) {
  .middle-metrics-list {
    grid-template-columns: 1fr;
  }
  .analysis-panel,
  .analysis-form {
    grid-template-columns: 1fr;
  }
  .middle-metric-row {
    border-right: 0;
    padding-left: 10px;
    padding-right: 10px;
  }
}
</style>
